/**
 * useDraftAutosave — E-93 Story 93.11.
 *
 * Sally UX: every N seconds, persist a serializable draft of the current
 * campaign (template, language, variables) to localStorage. On mount we
 * load whatever's there so a browser-crash or accidental-tab-close
 * doesn't nuke the user's work.
 *
 * Design picks:
 *
 * - **Serializable-only shape.** We deliberately exclude the partner
 *   selection `Set<string>` and the live query results — large, stale,
 *   and the partner list is re-fetched on mount anyway. Draft = the
 *   stuff the user typed that can't be reconstructed from server state.
 *
 * - **TTL gate.** A draft older than DRAFT_TTL_MS (7 days) is ignored
 *   on load — avoids reviving a campaign after a month-long
 *   vacation where the template may have been deleted.
 *
 * - **Per-tenant key** (`bulk-email-draft:{tenantId}`). A user with two
 *   tenants in separate tabs won't cross-contaminate drafts.
 *
 * - **Corruption-safe load.** If the stored JSON fails to parse or
 *   violates the schema, we treat it as "no draft" rather than
 *   throwing. A corrupted draft is the user's worst-case-lesson already.
 *
 * - **Save on blur (beforeunload) as backup.** The timer-based save
 *   covers the common case; the `beforeunload` listener catches the
 *   tab-close race where 29s of edits would otherwise be lost.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
export const DEFAULT_AUTOSAVE_INTERVAL_MS = 30_000;

export interface DraftEnvelope<T> {
  /** Millisecond timestamp of the last save. */
  savedAt: number;
  /** Schema version — bump on breaking changes so old drafts are ignored. */
  version: 1;
  /** The caller's serializable state. */
  data: T;
}

export interface UseDraftAutosaveOptions<T> {
  key: string;
  data: T;
  /** Save interval in ms. Defaults to 30s. */
  intervalMs?: number | undefined;
  /** Short-circuit — when false, nothing is saved or loaded. */
  enabled?: boolean | undefined;
  /** Runtime schema check. Return false to discard a loaded draft. */
  validate?: ((value: unknown) => value is T) | undefined;
}

export interface UseDraftAutosaveResult<T> {
  /** The draft that was present on mount, or null. */
  loadedDraft: DraftEnvelope<T> | null;
  /** Forcibly persist the current `data` now (beyond the timer cycle). */
  saveNow: () => void;
  /** Clear the draft from storage — call after a successful send. */
  clearDraft: () => void;
  /** True if a draft has been saved at least once this session. */
  hasUnsavedChanges: boolean;
}

function readLocalStorage(key: string): string | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
  } catch {
    // Private mode, quota, or storage disabled — all benign here.
    return null;
  }
}

function writeLocalStorage(key: string, value: string): boolean {
  try {
    if (typeof window === 'undefined') return false;
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeLocalStorage(key: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(key);
  } catch {
    // noop
  }
}

/**
 * Parse a stored draft envelope. Returns `null` on any failure mode:
 * missing key, malformed JSON, wrong shape, expired TTL, failed validator.
 * Exported for unit testing — the runtime code path calls this on mount.
 */
export function parseStoredDraft<T>(
  raw: string | null,
  validate: ((value: unknown) => value is T) | undefined,
  now: number = Date.now()
): DraftEnvelope<T> | null {
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const env = parsed as Partial<DraftEnvelope<T>>;
  if (env.version !== 1 || typeof env.savedAt !== 'number') return null;
  if (now - env.savedAt > DRAFT_TTL_MS) return null;
  if (validate && !validate(env.data)) return null;
  return env as DraftEnvelope<T>;
}

export function useDraftAutosave<T>(
  options: UseDraftAutosaveOptions<T>
): UseDraftAutosaveResult<T> {
  const {
    key,
    data,
    enabled = true,
    intervalMs = DEFAULT_AUTOSAVE_INTERVAL_MS,
    validate,
  } = options;

  // `data` might change every render — we capture the latest via ref so
  // the interval callback always serializes the freshest state without
  // restarting the timer.
  const dataRef = useRef(data);
  dataRef.current = data;

  const [loadedDraft, setLoadedDraft] = useState<DraftEnvelope<T> | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const loadedOnce = useRef(false);

  // Load on mount. We only do this once — subsequent re-renders never
  // re-read localStorage so a user's edits aren't clobbered by a ghost
  // of their earlier state.
  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    if (!enabled) return;
    const raw = readLocalStorage(key);
    const draft = parseStoredDraft<T>(raw, validate);
    if (draft) setLoadedDraft(draft);
  }, [enabled, key, validate]);

  const saveNow = useCallback(() => {
    if (!enabled) return;
    const envelope: DraftEnvelope<T> = {
      savedAt: Date.now(),
      version: 1,
      data: dataRef.current,
    };
    const ok = writeLocalStorage(key, JSON.stringify(envelope));
    if (ok) setHasUnsavedChanges(true);
  }, [enabled, key]);

  const clearDraft = useCallback(() => {
    removeLocalStorage(key);
    setLoadedDraft(null);
    setHasUnsavedChanges(false);
  }, [key]);

  // Timer-based save. We don't save on every render (too spammy) nor
  // only on blur (loses work on browser crash) — the 30s interval is
  // the middle ground Gmail and Google Docs converged on.
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(saveNow, intervalMs);
    return () => clearInterval(interval);
  }, [enabled, intervalMs, saveNow]);

  // Backup save on tab close. Covers the 0..intervalMs race.
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const handler = () => saveNow();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [enabled, saveNow]);

  return { loadedDraft, saveNow, clearDraft, hasUnsavedChanges };
}
